import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ProjectDetails, PipeSizeSet } from '../types'
import { apiFetch } from '../config/api'
import {
  fetchHubProjects,
  hubOpenPath,
  hubProjectToDetails,
  type Data360SyncedPipeSet,
  type HubProject
} from '../api/data360'

export interface EnsureWorkspaceResult {
  shadowProjectId: string
  project?: ProjectDetails
  pipeSets: Data360SyncedPipeSet[]
}

interface ProjectsContextValue {
  projects: ProjectDetails[]
  hubProjects: HubProject[]
  hubWarnings: string[]
  /** True while hub list is loading (Project Hub). */
  loading: boolean
  /** True while local /api/projects is loading (project routes). */
  projectsLoading: boolean
  error: string | null
  addProject: (project: ProjectDetails) => Promise<ProjectDetails>
  getProject: (id: string) => ProjectDetails | undefined
  updateProject: (project: ProjectDetails) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  refreshProjects: () => Promise<void>
  refreshHubProjects: () => Promise<void>
  refreshLocalProjects: () => Promise<void>
  patchProjectPipeSets: (projectId: string, pipeSets: PipeSizeSet[]) => void
  patchProjectFromEnsure: (result: EnsureWorkspaceResult) => void
  patchProjectInList: (project: ProjectDetails) => void
  fetchProjectById: (id: string) => Promise<ProjectDetails>
  openHubProject: (project: HubProject) => Promise<string>
  hubSearchQuery: string
  setHubSearchQuery: (query: string) => void
}

const ProjectsContext = createContext<ProjectsContextValue | undefined>(undefined)

function isSparseLinkedProject(project: ProjectDetails): boolean {
  if (!project.data360ProjectId) return false
  return (
    !String(project.location ?? '').trim() &&
    !(project.contractors?.length ?? 0) &&
    !(project.equipment?.length ?? 0)
  )
}

function mergeLinkedProjectSources(
  local: ProjectDetails,
  hub?: ProjectDetails
): ProjectDetails {
  if (!hub || !local.data360ProjectId) return local
  if (!isSparseLinkedProject(local)) return local
  return {
    ...hub,
    ...local,
    id: local.id,
    data360ProjectId: local.data360ProjectId ?? hub.data360ProjectId,
    createdFromApp: local.createdFromApp ?? hub.createdFromApp,
    pipeSets: local.pipeSets?.length ? local.pipeSets : (hub.pipeSets ?? [])
  }
}

export function ProjectsProvider({ children }: { children: React.ReactNode }) {
  const [projects, setProjects] = useState<ProjectDetails[]>([])
  const [hubProjects, setHubProjects] = useState<HubProject[]>([])
  const [hubWarnings, setHubWarnings] = useState<string[]>([])
  const [hubLoading, setHubLoading] = useState(true)
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hubSearchQuery, setHubSearchQuery] = useState('')
  const hubHasLoadedRef = React.useRef(false)
  const localHasLoadedRef = React.useRef(false)

  const refreshHubProjects = useCallback(async () => {
    // Silent refresh after first load so mechanical shell keeps Outlet mounted.
    const isCold = !hubHasLoadedRef.current
    try {
      if (isCold) setHubLoading(true)
      const hubData = await fetchHubProjects()
      setHubProjects(hubData.projects ?? [])
      setHubWarnings(hubData.warnings ?? [])
    } catch (err: any) {
      console.error('Failed to fetch hub projects:', err)
      setError(err.message || 'Failed to load hub projects')
    } finally {
      hubHasLoadedRef.current = true
      if (isCold) setHubLoading(false)
    }
  }, [])

  const refreshLocalProjects = useCallback(async () => {
    const isCold = !localHasLoadedRef.current
    try {
      if (isCold) setProjectsLoading(true)
      const localData = await apiFetch<ProjectDetails[]>('/api/projects')
      setProjects(localData)
    } catch (err: any) {
      console.error('Failed to fetch local projects:', err)
      setError(err.message || 'Failed to load projects')
    } finally {
      localHasLoadedRef.current = true
      if (isCold) setProjectsLoading(false)
    }
  }, [])

  const refreshProjects = useCallback(async () => {
    await Promise.all([refreshHubProjects(), refreshLocalProjects()])
  }, [refreshHubProjects, refreshLocalProjects])

  useEffect(() => {
    void refreshHubProjects()
    void refreshLocalProjects()
  }, [refreshHubProjects, refreshLocalProjects])

  const patchProjectPipeSets = useCallback((projectId: string, pipeSets: PipeSizeSet[]) => {
    setProjects((prev) =>
      prev.map((item) => (item.id === projectId ? { ...item, pipeSets } : item))
    )
  }, [])

  const patchProjectFromEnsure = useCallback((result: EnsureWorkspaceResult) => {
    const project = result.project
    if (!project) {
      setProjects((prev) => {
        const exists = prev.some((p) => p.id === result.shadowProjectId)
        if (exists) {
          return prev.map((p) =>
            p.id === result.shadowProjectId ? { ...p, pipeSets: result.pipeSets } : p
          )
        }
        return [
          {
            id: result.shadowProjectId,
            name: '',
            client: '',
            application: '',
            location: '',
            status: 'Active',
            pipeSizeRange: '',
            targetWpsCount: 0,
            dueDate: '',
            pipeSets: result.pipeSets,
            data360ProjectId: null
          },
          ...prev
        ]
      })
      return
    }

    const merged: ProjectDetails = {
      ...(project as ProjectDetails),
      id: result.shadowProjectId,
      pipeSets: result.pipeSets
    }

    setProjects((prev) => {
      const withoutPhantom = prev.filter(
        (p) => p.id !== result.shadowProjectId && !p.id.startsWith('d360-shadow-')
      )
      const exists = withoutPhantom.some((p) => p.id === result.shadowProjectId)
      if (exists) {
        return withoutPhantom.map((p) => (p.id === result.shadowProjectId ? merged : p))
      }
      return [merged, ...withoutPhantom]
    })

    setHubProjects((prev) =>
      prev.map((hub) =>
        hub.wpsShadowId === result.shadowProjectId ||
        hub.data360ProjectId === merged.data360ProjectId
          ? { ...hub, wpsShadowId: result.shadowProjectId, hubKind: 'data360-open' as const }
          : hub
      )
    )
  }, [])

  const patchProjectInList = useCallback((project: ProjectDetails) => {
    setProjects((prev) => {
      const exists = prev.some((p) => p.id === project.id)
      if (exists) {
        return prev.map((p) => (p.id === project.id ? { ...p, ...project } : p))
      }
      return [project, ...prev]
    })
  }, [])

  const fetchProjectById = useCallback(async (id: string): Promise<ProjectDetails> => {
    const project = await apiFetch<ProjectDetails>(`/api/projects/${id}`)
    patchProjectInList(project)
    return project
  }, [patchProjectInList])

  const addProject = useCallback(async (project: ProjectDetails): Promise<ProjectDetails> => {
    const created = await apiFetch<ProjectDetails>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(project)
    })
    setProjects((prev) => [created, ...prev])
    return created
  }, [])

  const getProject = useCallback(
    (id: string) => {
      const local = projects.find((project) => project.id === id)
      const hub = hubProjects.find(
        (project) => project.id === id || project.wpsShadowId === id
      )
      const hubDetails = hub ? hubProjectToDetails(hub) : undefined
      if (!local) return hubDetails
      return mergeLinkedProjectSources(local, hubDetails)
    },
    [projects, hubProjects]
  )

  const updateProject = useCallback(async (project: ProjectDetails): Promise<void> => {
    const isWpsOwned =
      !project.data360ProjectId || project.createdFromApp === 'WPS-Studio'
    if (project.data360ProjectId && project.hubKind !== 'legacy' && !isWpsOwned) {
      const { updateData360ExtraDetails } = await import('../api/data360')
      await updateData360ExtraDetails(project.data360ProjectId, {
        contractors: project.contractors,
        endClients: project.endClients,
        applications: project.applications,
        weldingCodes: project.weldingCodes,
        clientWeldingSpecs: project.clientWeldingSpecs,
        equipment: project.equipment,
        dueDate: project.dueDate,
        pipeSizeRange: project.pipeSizeRange,
        targetWpsCount: project.targetWpsCount
      })
    }
    const updated = await apiFetch<ProjectDetails>(`/api/projects/${project.id}`, {
      method: 'PUT',
      body: JSON.stringify(project)
    })
    patchProjectInList(updated as ProjectDetails)
  }, [patchProjectInList])

  const deleteProject = useCallback(async (id: string): Promise<void> => {
    await apiFetch(`/api/projects/${id}`, { method: 'DELETE' })
    setProjects((prev) => prev.filter((item) => item.id !== id))
    setHubProjects((prev) => prev.filter((item) => item.wpsShadowId !== id && item.id !== id))
  }, [])

  const openHubProject = useCallback(async (project: HubProject): Promise<string> => {
    return hubOpenPath(project)
  }, [])

  const value = useMemo(
    () => ({
      projects,
      hubProjects,
      hubWarnings,
      loading: hubLoading,
      projectsLoading,
      error,
      addProject,
      getProject,
      updateProject,
      deleteProject,
      refreshProjects,
      refreshHubProjects,
      refreshLocalProjects,
      patchProjectPipeSets,
      patchProjectFromEnsure,
      patchProjectInList,
      fetchProjectById,
      openHubProject,
      hubSearchQuery,
      setHubSearchQuery
    }),
    [
      projects,
      hubProjects,
      hubWarnings,
      hubLoading,
      projectsLoading,
      error,
      addProject,
      getProject,
      updateProject,
      deleteProject,
      refreshProjects,
      refreshHubProjects,
      refreshLocalProjects,
      patchProjectPipeSets,
      patchProjectFromEnsure,
      patchProjectInList,
      fetchProjectById,
      openHubProject,
      hubSearchQuery
    ]
  )

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>
}

export function useProjects() {
  const context = useContext(ProjectsContext)
  if (!context) {
    throw new Error('useProjects must be used within ProjectsProvider')
  }
  return context
}

export function useHubProjects(): HubProject[] {
  const { hubProjects } = useProjects()
  return hubProjects
}

export { hubProjectToDetails, hubOpenPath }
